/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, September 22nd 2021, 11:44:01 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { STATUS_CODES } from "lib/api/types";
import { APIServicePathType, APIPathParameter, RequestMethodType, QueryParameters } from "lib/types";
import { logger } from "lib/logger";

export interface ProcessedAPIResponse {
  httpResponseStatusCode: number;
  data: any;
  hasErrors: boolean;
  errors: { [key: string]: any };
}

/**
 * @API Type generics explained
 *
 * @param T - The type of the API object. It can either be the type of the request body e.g. in a mutation request or the type of the reponse body e.g. fetching a list of items.
 * depending on what you want to do e.g. get a list of objects , T = ObjectType[], get an item T = ObjectType, Post an object T = ObjectType
 *
 * @param PT - Same as QT below but for path parameters i.e. APIPathParameter<ObjectType>[]
 *
 * @param QT - The type of the query parameters reference object in case they take a different type generic other than the object type e.g. getting a list of objects,
 * they return type may be T = ObjectType[] but the Query parameters are based on a single object i.e. QueryParameters<ObjectType>, so those need
 * to be distinguished
 *
 */

interface APIHeaders {
  [key: string]: any;
}

export interface APICallArgs<T, PT = T, QT = T> extends Omit<RequestInit, "method" | "body" | "headers"> {
  apiBaseURL?: string;
  apiServicePath: APIServicePathType;
  apiPathParams?: APIPathParameter<PT>[];
  apiQueryParams?: QueryParameters<QT>;
  method?: RequestMethodType;
  headers?: APIHeaders;
  body?: BodyInit;
  apiRedirectURLFieldName?: string;
  responseIsFile?: boolean;
  csrfToken?: string;
}

const buildQueryString = <QT>(apiQueryParams: QueryParameters<QT>) => {
  const getFieldValuePair = (key: keyof QueryParameters<QT>) => {
    if (Array.isArray(apiQueryParams[key])) {
      const values = apiQueryParams[key] as any[];

      if (values.length <= 0) return "";

      return values.reduce((prev, curr) => `${prev}&${key}=${curr}`, "");
    }

    return `${key}=${apiQueryParams[key]}`;
  };

  return Object.keys(apiQueryParams).length > 0
    ? "?".concat(
        Object.keys(apiQueryParams)
          .map((key) => getFieldValuePair(key))
          .join("&")
      )
    : "";
};

const buildPathParameters = <PT>(apiPathParams: APIPathParameter<PT>[]) => {
  return apiPathParams.length > 0
    ? apiPathParams
        .reduce((previous, current) => `${previous}/${current}`)
        .toString()
        .concat("/") // For Django backend, ensure all urls end with '/'
    : "";
};

interface BuildHeadersArgs {
  method: RequestMethodType;
  headers: APIHeaders;
}

const getCSRFTokenHeader = (csrfToken: string) => {
  return { "X-CSRFToken": csrfToken };
};

const buildHeaders = ({ method, headers, csrfToken }: BuildHeadersArgs & { csrfToken?: string }) => {
  const methodIsUnsafe = ["POST", "PUT", "PATCH", "DELETE"].includes(method);
  const csrftokenHeader = methodIsUnsafe && csrfToken ? getCSRFTokenHeader(csrfToken) : {}; // Making sure this is within this functions lexical scope so that we always get the latest token without a user having to referesh the page.
  const defaultHeaders = { "Content-Type": "application/json", ...csrftokenHeader };
  return headers ? { ...headers, ...csrftokenHeader } : defaultHeaders;
};

const _callAPI = async <T, PT, QT>(args: APICallArgs<T, PT, QT>): Promise<ProcessedAPIResponse> => {
  const {
    apiBaseURL = process.env.REACT_APP_ONESTOP_API_BASE_URL,
    apiServicePath,
    apiPathParams = [],
    apiQueryParams = {},
    method = "GET",
    headers = null,
    body = JSON.stringify({}), // Default body will be JSON but one can also pass a body for another encoding e.g. form-data
    //eslint-disable-next-line
    cache = "no-cache",
    credentials = "include", // set credentials to include so fetchAPI can use Cookies within CORS policy
    signal,
    apiRedirectURLFieldName = process.env.REACT_APP_API_REDIRECT_URL_FIELD_NAME,
    responseIsFile = false,
    csrfToken = null,
  } = args;

  const buildInit = (): RequestInit => {
    const requestInit: RequestInit = {
      method,
      headers: buildHeaders({ method, headers, csrfToken }),
      credentials,
      signal,
    };

    switch (method) {
      default:
        return requestInit;
      case "POST":
      case "PUT":
      case "PATCH":
        return { ...requestInit, body };
    }
  };

  const buildURL = () =>
    apiBaseURL.concat(
      apiServicePath.toString(),
      buildPathParameters(apiPathParams),
      buildQueryString<QT>(apiQueryParams)
    );

  const httpResponse = await fetch(buildURL(), buildInit());

  if (httpResponse.redirected && canRedirect(apiBaseURL, httpResponse.url)) {
    const redirectFieldName = apiRedirectURLFieldName ?? "next";
    window.location.href = `${httpResponse.url}?${redirectFieldName}=${window.location.href}`;
  }

  const processedAPIResponse: ProcessedAPIResponse = {
    httpResponseStatusCode: httpResponse.status,
    data: {},
    hasErrors: false,
    errors: {},
  };

  if (!httpResponse.ok) {
    if (httpResponse.status >= STATUS_CODES.ClientError && httpResponse.status <= STATUS_CODES.ClientClosedRequest) {
      // For client errors, we return the feedback as errors
      processedAPIResponse.errors = await httpResponse.json();
    }

    processedAPIResponse.hasErrors = true;

    const error_message = `An API Error occurred on the backend. HTTP Response Status Code: ${processedAPIResponse.httpResponseStatusCode}. Errors:`;
    logger.error(error_message, processedAPIResponse.errors);
  } else {
    if (responseIsFile) {
      try {
        processedAPIResponse.data = await httpResponse.blob();
      } catch (e) {
        console.error(e);
        processedAPIResponse.data = {}; // Likely no response body but a successful request, so we set empty data.
      }
    } else {
      try {
        processedAPIResponse.data = await httpResponse.json();
      } catch (e) {
        console.error(e);
        processedAPIResponse.data = {}; // Likely no response body but a successful request, so we set empty data.
      }
    }
  }

  return processedAPIResponse;
};

export const OSAPI = {
  GET: <T, PT, QT>(args: APICallArgs<T, PT, QT>) => _callAPI(args),

  POST: <T, PT, QT>(args: APICallArgs<T, PT, QT>) => _callAPI({ ...args, method: "POST" }),

  PUT: <T, PT, QT>(args: APICallArgs<T, PT, QT>) => _callAPI({ ...args, method: "PUT" }),

  PATCH: <T, PT, QT>(args: APICallArgs<T, PT, QT>) => _callAPI({ ...args, method: "PATCH" }),

  DELETE: <T, PT, QT>(args: APICallArgs<T, PT, QT>) => _callAPI({ ...args, method: "DELETE" }),
};

export const getProcessedAPIResponse = <T, PT, QT>(args: APICallArgs<T, PT, QT>): Promise<ProcessedAPIResponse> => {
  const { apiServicePath } = args;

  switch (args.method) {
    default:
      return OSAPI.GET({
        apiServicePath,
        ...args,
      });
    case "POST":
      return OSAPI.POST({
        apiServicePath,
        ...args,
      });
    case "PUT":
      return OSAPI.PUT({
        apiServicePath,
        ...args,
      });
    case "PATCH":
      return OSAPI.PATCH({
        apiServicePath,
        ...args,
      });
    case "DELETE":
      return OSAPI.DELETE({
        apiServicePath,
        ...args,
      });
  }
};

// We do not want to redirect if the redirect URL is an api URL.
const canRedirect = (apiBaseURL: string, URL: string) => !URL.startsWith(apiBaseURL);
